import CheckCircle from '@mui/icons-material/CheckCircle'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Close from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import PrescriptionImageGallery from './PrescriptionImageGallery'
import PastPatientReportsDialog from 'src/clinic/components/ClinicManageBookingsPage/pastPatientReports/PastPatientReportsDialog'
import MultiplePictureUploadButton from 'src/consumer/components/Helpers/PictureUploadButton/MultiplePictureUploadButton'
import { getEmrReportTypeBasedOnEventType } from 'src/clinic/components/ClinicManageBookingsPage/sendPrescriptionData/components/util'

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prescription-tabpanel-${index}`}
      aria-labelledby={`prescription-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function SendPrescriptionImages({
  uploadPrescriptionImages,
  setUploadPrescriptionImages,
  loading,
  setLoading,
  showGallery = false,
  bookedEventInfo,
  mentorUserId,
  galleryImagesSearch,
  patientHistoryGetApiSignature,
  fileUploadApiSignature = '/api/secure/doctor/prescription/file/upload',
  // New props for tracking upload status
  onUploadingChange,
  onUploadComplete,
}) {
  const [tabValue, setTabValue] = useState(0)
  const [imageViewDialog, setImageViewDialog] = useState({
    open: false,
    currentIndex: -1,
    images: [],
  })

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const onSelectGalleryImage = (imageId) => {
    setUploadPrescriptionImages((prev) => {
      const imageExists = (prev || []).find((image) => image.id === imageId)
      console.log({ imageExists })
      if (!imageExists) {
        if (!(prev || []).includes(imageId)) {
          return [...(prev || []), imageId]
        }
      } else {
        return prev.filter((id) => id !== imageId)
      }
    })
  }

  const openImageViewer = (index, images) => {
    setImageViewDialog({ open: true, currentIndex: index, images })
  }

  const closeImageViewer = () => {
    setImageViewDialog({ open: false, currentIndex: -1, images: [] })
  }

  const handleNext = () => {
    setImageViewDialog((prev) => ({
      ...prev,
      currentIndex: Math.min(prev.currentIndex + 1, prev.images.length - 1),
    }))
  }

  const handlePrevious = () => {
    setImageViewDialog((prev) => ({
      ...prev,
      currentIndex: Math.max(prev.currentIndex - 1, 0),
    }))
  }

  const isCurrentImageSelected = () => {
    if (!imageViewDialog.images[imageViewDialog.currentIndex]) return false
    const fileId = imageViewDialog.images[imageViewDialog.currentIndex].fileId
    return (uploadPrescriptionImages || []).includes(fileId)
  }

  const handleToggleSelection = () => {
    if (imageViewDialog.images[imageViewDialog.currentIndex]) {
      const fileId = imageViewDialog.images[imageViewDialog.currentIndex].fileId
      onSelectGalleryImage(fileId)
    }
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!imageViewDialog.open) return

      if (event.key === 'ArrowLeft') {
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        handleNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [imageViewDialog.open])

  // If gallery is not enabled, show only upload functionality
  if (!showGallery) {
    return (
      <div>
        <MultiplePictureUploadButton
          preferCamera
          imageIds={uploadPrescriptionImages}
          onAddImageId={(imageId) =>
            setUploadPrescriptionImages((prev) => [...(prev || []), imageId])
          }
          onDeleteImageId={(imageId) =>
            setUploadPrescriptionImages((prev) => prev.filter((id) => id !== imageId))
          }
          aspectWidth={9}
          aspectHeight={13}
          imageUploadUrl={fileUploadApiSignature}
          fileUploadApiParams={{
            bookedEventId: bookedEventInfo?.id,
          }}
          onUploadingChange={onUploadingChange}
          onUploadComplete={onUploadComplete}
        />
      </div>
    )
  }

  return (
    <div>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="prescription image tabs">
          <Tab label="Upload Images" id="prescription-tab-0" />
          <Tab label="Choose from Gallery" id="prescription-tab-1" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <MultiplePictureUploadButton
          preferCamera
          imageIds={uploadPrescriptionImages}
          onAddImageId={(imageId) =>
            setUploadPrescriptionImages((prev) => [...(prev || []), imageId])
          }
          onDeleteImageId={(imageId) =>
            setUploadPrescriptionImages((prev) => prev.filter((id) => id !== imageId))
          }
          aspectWidth={9}
          aspectHeight={13}
          imageUploadUrl={fileUploadApiSignature}
          fileUploadApiParams={{
            bookedEventId: bookedEventInfo?.id,
            emrReportType: getEmrReportTypeBasedOnEventType(bookedEventInfo?.eventType),
          }}
          onUploadingChange={onUploadingChange}
          onUploadComplete={onUploadComplete}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <PrescriptionImageGallery
          userId={mentorUserId}
          onSelectImage={onSelectGalleryImage}
          onViewImage={openImageViewer}
          selectedImages={uploadPrescriptionImages}
          appFileSearchParams={galleryImagesSearch}
        />
      </TabPanel>

      {/* Image Zoom/View Dialog */}
      <Dialog
        open={imageViewDialog.open}
        onClose={closeImageViewer}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 1,
            position: 'relative',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={closeImageViewer}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <Close />
          </IconButton>

          {/* Image Counter and Select Button */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            {imageViewDialog.images.length > 1 && (
              <Typography
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  padding: '4px 12px',
                  borderRadius: '16px',
                }}
              >
                {imageViewDialog.currentIndex + 1} / {imageViewDialog.images.length}
              </Typography>
            )}
            {imageViewDialog.images[imageViewDialog.currentIndex] && (
              <Button
                onClick={handleToggleSelection}
                variant={isCurrentImageSelected() ? 'contained' : 'outlined'}
                size="small"
                startIcon={isCurrentImageSelected() ? <CheckCircle /> : null}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  backgroundColor: isCurrentImageSelected()
                    ? '#4caf50'
                    : 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: isCurrentImageSelected()
                      ? '#45a049'
                      : 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'white',
                  },
                }}
              >
                {isCurrentImageSelected() ? 'Selected' : 'Select'}
              </Button>
            )}
          </Box>

          {/* Previous Button */}
          {imageViewDialog.images.length > 1 && (
            <IconButton
              onClick={handlePrevious}
              disabled={imageViewDialog.currentIndex === 0}
              sx={{
                position: 'absolute',
                left: 16,
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
          )}

          {/* Image */}
          {imageViewDialog.images[imageViewDialog.currentIndex] && (
            <img
              src={`https://media.aumhum.com/images/prod/${
                imageViewDialog.images[imageViewDialog.currentIndex].fileId
              }`}
              alt="Prescription"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
          )}

          {/* Next Button */}
          {imageViewDialog.images.length > 1 && (
            <IconButton
              onClick={handleNext}
              disabled={imageViewDialog.currentIndex === imageViewDialog.images.length - 1}
              sx={{
                position: 'absolute',
                right: 16,
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
